import type { SnEvent } from './types'

/**
 * Which occurrences an edit or delete applies to. Sent as `?scope=` on
 * `PUT`/`DELETE /v1/events/{event}`; omitted for a one-off event.
 */
export type EventSeriesScope = 'this' | 'following' | 'all'

export interface SeriesScopeOption {
  scope: EventSeriesScope
  label: string
}

/**
 * Pre-selected scope: the single occurrence.
 *
 * The narrowest choice on purpose — a mis-click must not rewrite or wipe a
 * whole series.
 */
export const SERIES_SCOPE_DEFAULT: EventSeriesScope = 'this'

/** Ordered least-to-most broad, so the safest option is the first one read. */
export const DELETE_SCOPE_OPTIONS: SeriesScopeOption[] = [
  { scope: 'this', label: 'Delete This Event' },
  { scope: 'following', label: 'Delete Following Events In This Series' },
  { scope: 'all', label: 'Delete All Events In This Series' },
]

/** Same scopes, worded for the edit form's own verb. */
export const EDIT_SCOPE_OPTIONS: SeriesScopeOption[] = [
  { scope: 'this', label: 'This Event' },
  { scope: 'following', label: 'This And Following Events' },
  { scope: 'all', label: 'All Events In This Series' },
]

/**
 * Whether an event belongs to a repeating series, and so should be offered the
 * scope choices rather than acted on directly.
 *
 * Checks `series_id` first (the flat field the detail response sends) and
 * falls back to the nested `series` object, so a partial shape carrying only
 * one of the two is still recognised.
 */
export const isSeriesEvent = (event?: SnEvent | null): boolean =>
  Boolean(event?.series_id || event?.series?.id)
